import { Link } from 'react-router-dom';
import UpgradePrompt from './UpgradePrompt';

export type StripeBillingGateVariant = 'banner' | 'card' | 'compact';

interface StripeBillingGateProps {
  canUseStripeBilling: boolean;
  isStripeConnected: boolean | null;
  variant?: StripeBillingGateVariant;
  className?: string;
  collapsibleUpgrade?: boolean;
  upgradeCollapseStorageKey?: string;
}

function ConnectStripeCard({ className = '' }: { className?: string }) {
  return (
    <div
      className={`flex flex-col items-center justify-center py-4 text-gray-400 dark:text-gray-500 ${className}`}
    >
      <div className="text-4xl mb-2">💳</div>
      <p className="text-sm font-medium mb-2 text-gray-600 dark:text-gray-300">
        Connect Stripe to view payment data
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400 text-center mb-3">
        Connect your Stripe account in Settings → Integrations to see payment
        information.
      </p>
      <Link
        to="/app/settings/integrations"
        className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500"
      >
        Go to Integrations
      </Link>
    </div>
  );
}

function UpgradeBillingCard({ className = '' }: { className?: string }) {
  return (
    <div
      className={`flex flex-col items-center justify-center py-4 text-gray-400 dark:text-gray-500 ${className}`}
    >
      <div className="text-4xl mb-2">💳</div>
      <p className="text-sm font-medium mb-2 text-gray-600 dark:text-gray-300">
        Upgrade to view payment data
      </p>
      <p className="text-xs text-gray-500 dark:text-gray-400 text-center mb-3">
        Stripe billing and payment analytics are available on paid DojoTrack plans.
      </p>
      <Link
        to="/app/settings/billing"
        className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:text-indigo-500"
      >
        View upgrade options
      </Link>
    </div>
  );
}

function ConnectStripeBanner({ className = '' }: { className?: string }) {
  return (
    <div
      className={`mb-6 p-4 bg-blue-50 dark:bg-blue-900/20 border border-blue-100 dark:border-blue-800 rounded flex flex-col items-center text-center ${className}`}
    >
      <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-2">
        Sync with Stripe for Full Features
      </h3>
      <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">
        Connect your Stripe account to enable payment processing, automatic
        billing, membership subscriptions, and payment tracking. You can still
        manage students without Stripe, but payment-related features will be
        limited.
      </p>
      <div className="flex flex-wrap justify-center gap-3">
        <Link
          to="/app/settings/sync"
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
        >
          Sync with Stripe
        </Link>
        <Link
          to="/app/settings/integrations"
          className="inline-flex items-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-sm font-medium rounded-md shadow-sm text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
        >
          Connect Stripe
        </Link>
      </div>
    </div>
  );
}

export default function StripeBillingGate({
  canUseStripeBilling,
  isStripeConnected,
  variant = 'card',
  className = '',
  collapsibleUpgrade = false,
  upgradeCollapseStorageKey,
}: StripeBillingGateProps) {
  if (isStripeConnected === true) {
    return null;
  }

  if (!canUseStripeBilling) {
    if (variant === 'banner') {
      return (
        <UpgradePrompt
          capability="stripeBilling"
          className={`mb-6 ${className}`}
          collapsible={collapsibleUpgrade}
          collapseStorageKey={upgradeCollapseStorageKey}
        />
      );
    }
    return <UpgradeBillingCard className={className} />;
  }

  if (isStripeConnected === null) {
    if (variant === 'banner') {
      return null;
    }
    return (
      <div className={`flex justify-center items-center py-4 ${className}`}>
        <div className="animate-pulse flex space-x-4 items-center">
          <div className="h-5 w-5 bg-indigo-200 dark:bg-indigo-600 rounded-full animate-spin" />
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Preparing payment data...
          </div>
        </div>
      </div>
    );
  }

  if (variant === 'banner') {
    return <ConnectStripeBanner className={className} />;
  }

  return <ConnectStripeCard className={className} />;
}

export function StripeBillingCompactMessage({
  canUseStripeBilling,
  isStripeConnected,
}: {
  canUseStripeBilling: boolean;
  isStripeConnected: boolean | null;
}) {
  if (isStripeConnected === true) {
    return null;
  }

  if (isStripeConnected === null) {
    return (
      <span className="text-gray-400 dark:text-gray-500 text-sm">
        Loading...
      </span>
    );
  }

  if (!canUseStripeBilling) {
    return (
      <Link
        to="/app/settings/billing"
        className="text-gray-400 dark:text-gray-500 text-sm hover:text-indigo-600 dark:hover:text-indigo-400"
      >
        Upgrade to view
      </Link>
    );
  }

  return (
    <Link
      to="/app/settings/integrations"
      className="text-gray-400 dark:text-gray-500 text-sm hover:text-indigo-600 dark:hover:text-indigo-400"
    >
      Connect Stripe to view
    </Link>
  );
}
